import {
  batch,
  computed,
  effect,
  signal,
  type ReadonlySignal,
} from "@preact/signals";
import type { LoginManager } from "../managers/LoginManager";
import type { BibleSelectorState } from "../managers/BibleSelectorManager";
import type { PanesManager } from "../managers/PanesManager";
import { createSidebar } from "../managers/SidebarManager";
import {
  getProfileConfigValue,
  saveProfileConfigValue,
} from "../managers/ProfileConfigSync";

type SidebarManager = ReturnType<typeof createSidebar>;

const TUTORIAL_SEEN_KEY = "sb-tutorial-seen";
const TUTORIAL_OPTED_OUT_KEY = "sb-tutorial-opted-out";
const TUTORIAL_FEATURES_KEY = "sb-tutorial-features-seen";

// Stored unprefixed on profile.config (matching `fontSize`, `lang`,
// etc.) — the backend record that the user has completed/skipped the tour.
const PROFILE_TUTORIAL_SEEN = "tutorialSeen";
const PROFILE_TUTORIAL_OPTED_OUT = "tutorialOptedOut";
const PROFILE_TUTORIAL_FEATURES_SEEN = "tutorialFeaturesSeen";

/** Where the popover sits relative to its highlighted target. */
export type TutorialPlacement = "top" | "bottom" | "left" | "right";

export interface TutorialStep {
  id: string;
  /** CSS selector for the element to spotlight. */
  target: string;
  /** i18n key + fallback for the step title. */
  titleKey: string;
  titleDefault: string;
  /** i18n key + fallback for the step body. */
  bodyKey: string;
  bodyDefault: string;
  /** Preferred popover placement; the component flips it if there's no room. */
  placement?: TutorialPlacement;
  /** Optional side effect run when the step becomes active (e.g. open a panel). */
  onEnter?: () => void;
  /** Optional cleanup run when leaving the step (e.g. close that panel). */
  onLeave?: () => void;
  /**
   * Steps whose target lives inside the book-selector portal are grouped as
   * "selector" so the tour overlay can render in that same portal (otherwise it
   * would be hidden behind the selector, which is its own stacking context).
   */
  group?: "selector";
  /**
   * Lifts the tour overlay above high z-index app panels (e.g. the mobile
   * settings sheet) so the spotlight + popover render on top of what's open,
   * instead of behind it. Used by contextual tips that fire while a panel is up.
   */
  elevated?: boolean;
  /**
   * How the target is picked out. `"spotlight"` (the default) dims the whole
   * UI around it, which suits a tour that has taken over the screen.
   * `"glow"` leaves the page alone and rings the target in the accent colour
   * instead — for a one-off tip about a single small control, where blacking
   * out the page says more than the tip is worth.
   */
  highlight?: "spotlight" | "glow";
}

/**
 * First-run onboarding tour: just the basics needed to start reading. Advanced
 * surfaces (pane layout, add-tab, search) are taught contextually the first
 * time the user interacts with them — see CONTEXTUAL_TUTORIALS.
 */
export const ONBOARDING_STEPS: TutorialStep[] = [
  {
    id: "selector-books",
    target: ".sidebar-results",
    titleKey: "tutorial.selectorBooksTitle",
    titleDefault: "Select books and chapters",
    bodyKey: "tutorial.selectorBooksBody",
    bodyDefault: "Choose a chapter from here to begin reading.",
    placement: "top",
    group: "selector",
  },
  {
    id: "selector-translation",
    target: ".sidebar-translation-selector",
    titleKey: "tutorial.selectorTranslationTitle",
    titleDefault: "Select a Bible text",
    bodyKey: "tutorial.selectorTranslationBody",
    bodyDefault: "Choose from any of the available versions.",
    placement: "bottom",
    group: "selector",
  },
  {
    id: "selector-testament",
    target: ".dropdown",
    titleKey: "tutorial.selectorTestamentTitle",
    titleDefault: "Filter book options",
    bodyKey: "tutorial.selectorTestamentBody",
    bodyDefault:
      "Show all books, or narrow to just the Old Testament, New Testament, or extra-biblical writings.",
    placement: "bottom",
    group: "selector",
  },
  {
    id: "tabs",
    target: ".sb-sidebar-tabs-header",
    titleKey: "tutorial.tabsTitle",
    titleDefault: "Tabs and saves",
    bodyKey: "tutorial.tabsBody",
    bodyDefault: "Your open passages and saves live here.",
    placement: "right",
  },
  {
    id: "toolbar",
    target: ".sb-reader-toolbar",
    titleKey: "tutorial.toolbarTitle",
    titleDefault: "Toolbar",
    bodyKey: "tutorial.toolbarBody",
    bodyDefault:
      "You can customize the toolbar to your liking. By default, book selection, navigation, and search are built in.",
    placement: "top",
  },
  {
    id: "settings",
    target: '[data-tutorial="settings"]',
    titleKey: "tutorial.settingsTitle",
    titleDefault: "Settings",
    bodyKey: "tutorial.settingsBody",
    bodyDefault:
      "Customize themes, text, and more here, or install the app to your device.",
    placement: "right",
  },
];

/**
 * Back-compat alias. Some callers (settings page replay, mobile tour
 * fallbacks) still reference TUTORIAL_STEPS by name.
 */
export const TUTORIAL_STEPS = ONBOARDING_STEPS;

/**
 * The mobile tour. Below 480px the desktop selector sub-controls (translation,
 * testament) and the pane-layout menu aren't rendered, so mobile gets its own
 * step list targeting the mobile header, the book selector grid, and the
 * bottom toolbar. The book step reuses the `selector-books` id so the
 * selector's built-in spotlight/open logic applies.
 */
export const MOBILE_TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: "m-passage",
    target: ".sb-bible-reader-mobile-header-title",
    titleKey: "tutorial.mobilePassageTitle",
    titleDefault: "Your current translation",
    bodyKey: "tutorial.mobilePassageBody",
    bodyDefault: "Tap the translation name to jump to a different translation.",
    placement: "bottom",
  },
  {
    id: "m-books",
    target: ".sb-reader-floating-nav-label",
    titleKey: "tutorial.mobileBooksTitle",
    titleDefault: "Your book/chapter selection",
    bodyKey: "tutorial.mobileBooksBody",
    bodyDefault: "Tap the book name to jump to a different book or chapter.",
    placement: "top",
  },
  {
    id: "selector-books",
    target: ".sidebar-results",
    titleKey: "tutorial.selectorBooksTitle",
    titleDefault: "Select books and chapters",
    bodyKey: "tutorial.selectorBooksBody",
    bodyDefault: "Choose a chapter from here to begin reading.",
    placement: "bottom",
    group: "selector",
  },
  {
    id: "toolbar",
    target: ".sb-reader-toolbar",
    titleKey: "tutorial.toolbarTitle",
    titleDefault: "Toolbar",
    bodyKey: "tutorial.mobileToolbarBody",
    bodyDefault:
      "Switch between the Bible, search, and more from the bottom bar.",
    placement: "top",
  },
  // Settings is taught contextually (CONTEXTUAL_TUTORIALS["mobile-settings"]) —
  // triggered when the user opens the settings sheet, not as a forced tour step.
  // (A modal tour overlay can't be tapped "through" to the real button, so the
  // tip is fired from the button's own handler instead.)
];

/**
 * Contextual single-feature tutorials. Triggered the first time the user
 * actually uses the feature (e.g. clicks the panels button), so we don't
 * front-load advanced UI in the first-run experience. Each feature gets its
 * own seen-flag so a completed/skipped contextual tour never reappears.
 */
export const CONTEXTUAL_TUTORIALS: Record<string, TutorialStep[]> = {
  "mobile-settings": [
    {
      id: "mobile-settings",
      // Fired from the settings button's own handler once the sheet is open, so
      // it spotlights what's now on screen. `elevated` lifts the tour layer above
      // the sheet (which sits at a very high z-index), otherwise the tip hides
      // behind it.
      target: ".sb-mobile-settings-sheet",
      titleKey: "tutorial.mobileSettingsTitle",
      titleDefault: "Settings",
      bodyKey: "tutorial.mobileSettingsBody",
      bodyDefault: "Customize text, themes, and reading options here.",
      placement: "top",
      elevated: true,
    },
  ],
  "pane-layout": [
    {
      id: "pane-layout",
      // The menu is opened by the Sidebar while this step is active; spotlight
      // it (it holds the layout options) rather than just the button.
      target: ".sb-pane-layout-menu",
      titleKey: "tutorial.paneLayoutTitle",
      titleDefault: "Arrange your panes",
      bodyKey: "tutorial.paneLayoutBody",
      bodyDefault:
        "Choose how many passages to read side by side from this layout menu.",
      placement: "right",
    },
  ],
  "add-tab": [
    {
      id: "add-tab",
      target: ".sb-tab-add-button",
      titleKey: "tutorial.addTabTitle",
      titleDefault: "Open more passages",
      bodyKey: "tutorial.addTabBody",
      bodyDefault:
        "Add a new tab to read several books or translations side by side.",
      placement: "left",
    },
  ],
  "offline-download": [
    {
      id: "offline-download",
      // Fired when the translation list opens, which is the only place the
      // download control exists. Grouped as "selector" so the selector draws
      // it: its nodes live in a CasualOS shadow root the generic overlay can't
      // measure.
      target: ".sb-offline-btn",
      titleKey: "tutorial.offlineDownloadTitle",
      titleDefault: "Tap to keep it on your device",
      bodyKey: "tutorial.offlineDownloadBody",
      bodyDefault:
        "Any translation with this icon can be saved for offline reading.",
      placement: "bottom",
      group: "selector",
      highlight: "glow",
    },
  ],
  search: [
    {
      id: "selector-search",
      target: ".searchbar",
      titleKey: "tutorial.selectorSearchTitle",
      titleDefault: "Search by",
      bodyKey: "tutorial.selectorSearchBody",
      bodyDefault: "Search by chapter, verse or book from here.",
      placement: "bottom",
      group: "selector",
    },
  ],
};

/**
 * ID of the first-run onboarding tour. Picks the desktop or mobile step list
 * for the current viewport, so the same link works on both.
 */
export const INTRODUCTION_TUTORIAL_ID = "introduction";

/**
 * Tutorials that can be launched from a `?tutorial=<id>` link. Keep
 * docs/tutorial-links.md in sync. `mobile-settings` and `offline-download`
 * are left out: they spotlight a sheet/list that only exists after the user
 * opens it, so launched cold they would point at nothing.
 */
export const LINKABLE_TUTORIAL_IDS: readonly string[] = [
  INTRODUCTION_TUTORIAL_ID,
  "pane-layout",
  "add-tab",
  "search",
];

/**
 * Linkable tutorials whose target is only rendered on desktop. A link to one
 * of these is ignored on a mobile viewport rather than spotlighting nothing.
 */
const DESKTOP_ONLY_TUTORIAL_IDS: readonly string[] = ["pane-layout"];

/** A tutorial requested by the page URL (`?tutorial=` / `?tutorialStep=`). */
export interface TutorialLinkRequest {
  id: string;
  step: number;
}

/**
 * Reads a tutorial request from `?tutorial=<id>&tutorialStep=<n>`. Returns
 * null for a missing or unknown id; a missing or malformed step means 0.
 */
export function parseTutorialLink(
  params: URLSearchParams
): TutorialLinkRequest | null {
  const id = params.get("tutorial");
  if (!id || !LINKABLE_TUTORIAL_IDS.includes(id)) {
    return null;
  }
  const rawStep = params.get("tutorialStep") ?? "";
  return { id, step: /^\d+$/.test(rawStep) ? Number(rawStep) : 0 };
}

/**
 * Mirrors the running tutorial into `?tutorial=&tutorialStep=` so the page can
 * be shared or refreshed mid-tour and land back on the same step. Replaces
 * rather than pushes: stepping through a tour shouldn't fill Back history.
 * Only linkable tutorials are written; anything else counts as "not running",
 * so a link never advertises a tour that won't replay. While `linkedTutorial`
 * is pending its params are kept until it launches; otherwise stale params
 * (e.g. an unlinkable id) are cleared right away.
 */
export function mirrorTutorialToUrl(
  tutorial: Pick<TutorialManager, "activeTutorialId" | "index">,
  navigation: {
    updateQueryParams: (
      update: Record<string, string | null>,
      replaceState?: boolean
    ) => void;
  },
  linkedTutorial: TutorialLinkRequest | null
): () => void {
  let shouldClear = linkedTutorial === null;
  return effect(() => {
    const rawId = tutorial.activeTutorialId.value;
    const step = tutorial.index.value;
    const id = rawId && LINKABLE_TUTORIAL_IDS.includes(rawId) ? rawId : null;
    if (id) {
      shouldClear = true;
      navigation.updateQueryParams(
        { tutorial: id, tutorialStep: String(step) },
        true
      );
    } else if (shouldClear) {
      navigation.updateQueryParams(
        { tutorial: null, tutorialStep: null },
        true
      );
    }
  });
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    window.localStorage.setItem(key, "true");
  } catch {
    // Best-effort; the profile record is the durable source of truth.
  }
}

function readFeaturesFlag(key: string): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, boolean>;
    }
  } catch {
    // ignore
  }
  return {};
}

function writeFeaturesFlag(key: string, value: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Best-effort; profile record is the durable source of truth.
  }
}

/** Which tour is currently running — controls what `finish()` records. */
type TutorialMode = "onboarding-desktop" | "onboarding-mobile" | "contextual";

export interface TutorialManager {
  /** All steps in the currently active tour. */
  steps: TutorialStep[];
  /** Whether the tour is currently showing. */
  running: ReadonlySignal<boolean>;
  /** Index of the active step while running. */
  index: ReadonlySignal<number>;
  /** The active step, or null when not running. */
  currentStep: ReadonlySignal<TutorialStep | null>;
  /**
   * Whether the active step is the last in the linear queue (so its primary
   * button reads "Done"). Click-only interjections always report `true` — their
   * primary button just dismisses the tip back to the tour.
   */
  isLast: ReadonlySignal<boolean>;
  /** Whether the tour can step backwards from the active step. */
  canGoBack: ReadonlySignal<boolean>;
  /**
   * ID of the running tutorial ({@link INTRODUCTION_TUTORIAL_ID} or a
   * contextual feature id), or null when none is running.
   */
  activeTutorialId: ReadonlySignal<string | null>;
  /** Whether the user has already completed/skipped the onboarding tour. */
  completed: ReadonlySignal<boolean>;
  /** Whether the user has opted out of all future tutorial prompts. */
  optedOut: ReadonlySignal<boolean>;
  /**
   * Whether the first-run offer card ("Would you like a tutorial?") is showing.
   * Set once for new users after onboarding finishes, instead of launching the
   * tour unannounced. Resolved by {@link acceptPrompt} / {@link dismissPrompt}.
   */
  promptVisible: ReadonlySignal<boolean>;
  /**
   * Whether the "turn off all tutorials?" follow-up dialog is showing. Raised
   * by {@link skip} once the tour it interrupted has ended, and resolved by
   * {@link keepTutorials} / {@link optOut}.
   */
  skipPromptVisible: ReadonlySignal<boolean>;
  /**
   * True for the rest of this visit after the user declines the introduction
   * offer ("No, thanks") or leaves that tour before finishing it. Not stored.
   * The install prompt stays hidden while this is set and can still appear on
   * the next visit; the download prompt stays hidden too, because the install
   * offer never resolves this visit. A contextual tip does not set it.
   */
  leftIntroductionEarly: ReadonlySignal<boolean>;
  /** Per-feature contextual tutorial completion flags. */
  featuresSeen: ReadonlySignal<Record<string, boolean>>;
  /** Starts (or restarts) the onboarding tour from the first step. */
  start: () => void;
  /**
   * Starts a contextual single-feature tour, if not already seen and the user
   * hasn't opted out. Safe to call from event handlers without pre-checking.
   *
   * `steps` overrides the `CONTEXTUAL_TUTORIALS[featureId]` lookup — for a
   * tour whose steps need instance state (e.g. the id of the record a step's
   * `onEnter` should open) that isn't available at module load. The "seen"
   * flag is still tracked under `featureId` either way.
   */
  startContextual: (featureId: string, steps?: TutorialStep[]) => void;
  /**
   * Starts a tutorial by id at `step` (clamped to the tour's length), whether
   * or not the user has seen it or opted out — this is an explicit request,
   * e.g. from a link. Returns false for an unknown id.
   */
  startTutorial: (id: string, step?: number) => boolean;
  /** Advances to the next step, finishing after the last one. */
  next: () => void;
  /** Goes back one step (no-op on the first). */
  prev: () => void;
  /** Ends the tour, recording completion for the active tour type. */
  finish: () => void;
  /**
   * Ends the current tour (like {@link finish}) and raises the "turn off all
   * tutorials?" follow-up dialog, rather than asking that question on the
   * tour dialog itself. Leaving the introduction tour this way also keeps the
   * install and download prompts from opening afterwards.
   */
  skip: () => void;
  /** Dismisses the skip follow-up dialog, leaving future tutorials enabled. */
  keepTutorials: () => void;
  /**
   * Confirms the skip follow-up dialog: records that the user does not want
   * future tutorial prompts. Also usable directly (e.g. from Settings) to opt
   * out without going through the skip flow.
   */
  optOut: () => void;
  /** Accepts the first-run offer card: hides it and starts the onboarding tour. */
  acceptPrompt: () => void;
  /**
   * Declines the first-run offer card: hides it and records the onboarding tour
   * as seen (still replayable from Settings). Keeps the install and download
   * prompts from following it this visit.
   */
  dismissPrompt: () => void;

  /**
   * Applies the device's real "seen" / "opted out" / per-feature flags from
   * `localStorage`. They seed to their SSR values (false / false / empty), so
   * call this once from a post-mount effect — via `AppState.hydrateFromStorage`
   * — rather than reading at construction, where it would make a returning
   * visitor's first render disagree with the served HTML.
   */
  hydrateStoredFlags: () => void;

  /**
   * Starts watching for the moment the first-run offer card should appear.
   * Deliberately separate from construction: the condition it waits on is
   * already true before the client's first render, so arming it eagerly would
   * put the card in the client tree but not in the SSR HTML. Call once from a
   * post-mount effect (via `AppState.hydrateFromStorage`). Idempotent.
   */
  armAutoStart: () => void;
}

/**
 * Drives the guided coachmark tour. Offers itself once for new users once the
 * reader is open to a chapter and visible (no fullscreen pane covering it),
 * and can be replayed from Settings. Completion is recorded on the user's
 * profile (backend) plus a local cache.
 */
export function createTutorialManager(
  login: LoginManager,
  readerVisible: ReadonlySignal<boolean>,
  selector: BibleSelectorState,
  isMobile: ReadonlySignal<boolean>,
  panes: PanesManager,
  sidebar: SidebarManager,
  /**
   * The visit started from a shared link (a session invite, a playlist, a
   * reading plan). While true, neither the offer card nor contextual tips
   * appear. A signal because a playlist link lifts it once the visitor closes
   * the playlist without starting it and lands on the home screen.
   */
  openedViaContentLink: ReadonlySignal<boolean> = signal(false),
  /**
   * Compact partner-site embed. While this is true the offer card, the tour,
   * and the "turn off tutorials" follow-up never start — an embed has no room
   * for coach marks aimed at chrome that isn't there.
   */
  isMinimalEmbed: ReadonlySignal<boolean> = signal(false),
  linkedTutorial: TutorialLinkRequest | null = null
): TutorialManager {
  const running = signal<boolean>(false);
  const index = signal<number>(0);
  // First-run offer card visibility (see `promptVisible` in the interface).
  const promptVisible = signal<boolean>(false);
  // "Turn off all tutorials?" follow-up dialog visibility (see
  // `skipPromptVisible` in the interface).
  const skipPromptVisible = signal<boolean>(false);
  // See `leftIntroductionEarly`. A closure-lifetime signal, not storage: the
  // next page load starts over and may still offer install.
  const leftIntroductionEarly = signal<boolean>(false);

  // The active step set is chosen at `start()` / `startContextual()` time
  // (snapshotted so a resize mid-tour doesn't swap the steps out from under us).
  const mode = signal<TutorialMode>("onboarding-desktop");
  const activeFeatureId = signal<string | null>(null);
  const activeSteps = signal<TutorialStep[]>(ONBOARDING_STEPS);

  // Steps that spotlight elements inside the book selector. The selector is
  // kept open for the whole group (no open/close flicker between them) and
  // closed again once the tour moves past it.
  const SELECTOR_GROUP = new Set([
    "selector-books",
    "selector-translation",
    "selector-testament",
    "selector-search",
  ]);

  // Seeded to the values SSR sees (it has no `localStorage`) rather than read
  // eagerly, so the client's first render matches the served HTML.
  // `hydrateStoredFlags` applies the device's real flags after the first commit
  // — see `AppState.hydrateFromStorage`.
  const seenLocally = signal<boolean>(false);
  const optedOutLocally = signal<boolean>(false);
  const featuresSeenLocal = signal<Record<string, boolean>>({});

  const hydrateStoredFlags = () => {
    batch(() => {
      seenLocally.value = readFlag(TUTORIAL_SEEN_KEY);
      optedOutLocally.value = readFlag(TUTORIAL_OPTED_OUT_KEY);
      // Merged UNDER the current value so a flag set in the brief window before
      // this runs isn't clobbered by the now-stale disk snapshot.
      featuresSeenLocal.value = {
        ...readFeaturesFlag(TUTORIAL_FEATURES_KEY),
        ...featuresSeenLocal.value,
      };
    });
  };

  const completed = computed<boolean>(() => {
    if (seenLocally.value) {
      return true;
    }
    const fromProfile = getProfileConfigValue(
      login.profile.value,
      PROFILE_TUTORIAL_SEEN
    );
    return fromProfile === true || fromProfile === "true";
  });

  const optedOut = computed<boolean>(() => {
    if (optedOutLocally.value) {
      return true;
    }
    const fromProfile = getProfileConfigValue(
      login.profile.value,
      PROFILE_TUTORIAL_OPTED_OUT
    );
    return fromProfile === true || fromProfile === "true";
  });

  const featuresSeen = computed<Record<string, boolean>>(() => {
    const local = featuresSeenLocal.value;
    const fromProfile = getProfileConfigValue(
      login.profile.value,
      PROFILE_TUTORIAL_FEATURES_SEEN
    );
    if (fromProfile && typeof fromProfile === "object") {
      return {
        ...(fromProfile as Record<string, boolean>),
        ...local,
      };
    }
    return local;
  });

  const activeTutorialId = computed<string | null>(() => {
    if (!running.value) {
      return null;
    }
    return mode.value === "contextual"
      ? activeFeatureId.value
      : INTRODUCTION_TUTORIAL_ID;
  });

  const currentStep = computed<TutorialStep | null>(() =>
    running.value ? (activeSteps.value[index.value] ?? null) : null
  );

  const isLast = computed<boolean>(
    () => running.value && index.value >= activeSteps.value.length - 1
  );

  const canGoBack = computed<boolean>(() => running.value && index.value > 0);

  // Run each step's onEnter when it becomes active and the previous step's
  // onLeave when moving away (including when the tour ends).
  let prevStepId: string | null = null;
  effect(() => {
    const active = running.value
      ? (activeSteps.value[index.value] ?? null)
      : null;
    const activeId = active?.id ?? null;
    if (activeId === prevStepId) {
      return;
    }
    activeSteps.value.find((s) => s.id === prevStepId)?.onLeave?.();
    active?.onEnter?.();
    prevStepId = activeId;
  });

  // Keep the book selector open while any selector-group step is active, and
  // close it (only if the tour opened it) once the tour moves past the group.
  // Clicking the reader title reuses the app's own open logic (correct pane).
  let selectorOpenedByTour = false;
  effect(() => {
    const active = running.value
      ? (activeSteps.value[index.value] ?? null)
      : null;
    const inGroup = active ? SELECTOR_GROUP.has(active.id) : false;
    if (inGroup) {
      if (!selector.isOpen.value) {
        // Desktop opens via the reader title; mobile via the mobile header's
        // book label. Either reuses the app's own open logic (correct pane).
        const title = (document.querySelector(".sb-bible-reader-title") ??
          document.querySelector(
            ".sb-bible-reader-mobile-header-book"
          )) as HTMLElement | null;
        title?.click();
        selectorOpenedByTour = true;
      }
    } else if (selectorOpenedByTour) {
      void selector.setOpen(false);
      selectorOpenedByTour = false;
    }
  });

  // Open the translation dropdown while its step is active so the tour shows it
  // expanded, and collapse it again when the tour moves on (only if the tour
  // opened it — don't fight a user who opened it themselves).
  let translationOpenedByTour = false;
  effect(() => {
    const active = running.value
      ? (activeSteps.value[index.value] ?? null)
      : null;
    const wantTranslation = active?.id === "selector-translation";
    if (wantTranslation) {
      if (!selector.selectingTranslation.value) {
        selector.selectingTranslation.value = true;
        translationOpenedByTour = true;
      }
    } else if (translationOpenedByTour) {
      selector.selectingTranslation.value = false;
      translationOpenedByTour = false;
    }
  });

  const markOnboardingSeen = () => {
    writeFlag(TUTORIAL_SEEN_KEY);
    seenLocally.value = true;
    saveProfileConfigValue(login, PROFILE_TUTORIAL_SEEN, true);
  };

  const markFeatureSeen = (featureId: string) => {
    const next = { ...featuresSeenLocal.value, [featureId]: true };
    featuresSeenLocal.value = next;
    writeFeaturesFlag(TUTORIAL_FEATURES_KEY, next);

    // Merge with whatever the profile already has so we don't clobber other
    // features' flags written from another device.
    const existing = getProfileConfigValue(
      login.profile.value,
      PROFILE_TUTORIAL_FEATURES_SEEN
    );
    const base =
      existing && typeof existing === "object"
        ? (existing as Record<string, boolean>)
        : {};
    saveProfileConfigValue(login, PROFILE_TUTORIAL_FEATURES_SEEN, {
      ...base,
      [featureId]: true,
    });
  };

  const markOptedOut = () => {
    writeFlag(TUTORIAL_OPTED_OUT_KEY);
    optedOutLocally.value = true;
    saveProfileConfigValue(login, PROFILE_TUTORIAL_OPTED_OUT, true);
  };

  // Set once the first-run offer has been resolved — either the effect below
  // decided, or `start()` was called directly (the welcome screen's tour
  // button) before the reader was visible.
  let autoStartChecked = false;

  const start = (startIndex = 0) => {
    if (isMinimalEmbed.value) {
      return;
    }
    // An explicit start resolves the first-run offer. Marking that before
    // tearing Today down matters: closing the pane is what makes the reader
    // visible, and the offer effect would otherwise pop the card on top of
    // the tour it was waiting to show.
    autoStartChecked = true;
    promptVisible.value = false;

    // Close whatever overlapping UI is up first — coach marks target the
    // normal reader UI, so a fullscreen pane (e.g. the Today screen) or an
    // open sidebar panel left up would hide the very elements being
    // highlighted. Mirrors the teardown `BibleReaderToolbar` does before
    // showing reader UI over the Today screen.
    sidebar.closeSearchPanel();
    sidebar.closeChatPanel();
    sidebar.closeSettings();
    sidebar.closeSidebar();
    panes.closeAll();
    // The desktop tour spotlights the tabs header, which the collapsed rail
    // doesn't render. Open it so those steps have a target. Remembered, so
    // the sidebar stays open after the tour instead of snapping shut.
    if (!isMobile.value && sidebar.isSidebarCollapsed.value) {
      sidebar.setSidebarCollapsed(false);
    }

    // Pick the step set for the current viewport before showing the tour.
    mode.value = isMobile.value ? "onboarding-mobile" : "onboarding-desktop";
    activeSteps.value = isMobile.value
      ? MOBILE_TUTORIAL_STEPS
      : ONBOARDING_STEPS;
    activeFeatureId.value = null;
    if (activeSteps.value.length === 0) {
      return;
    }
    // Batched so a linked start at step N never briefly runs step 0 (whose
    // selector-group effect would flash the book selector open).
    batch(() => {
      index.value = Math.min(
        Math.max(0, Math.floor(startIndex)),
        activeSteps.value.length - 1
      );
      running.value = true;
    });
  };

  const startContextual = (featureId: string, steps?: TutorialStep[]) => {
    if (isMinimalEmbed.value) {
      return;
    }

    if (running.value) {
      return;
    }
    // On a content-link tab, don't pop contextual coach marks. Not persisted,
    // so these tips still appear on a normal (non-link) visit later.
    if (openedViaContentLink.value) {
      return;
    }
    if (optedOut.value) {
      return;
    }
    if (featuresSeen.value[featureId]) {
      return;
    }
    const resolvedSteps = steps ?? CONTEXTUAL_TUTORIALS[featureId];
    if (!resolvedSteps || resolvedSteps.length === 0) {
      return;
    }
    mode.value = "contextual";
    activeFeatureId.value = featureId;
    activeSteps.value = resolvedSteps;
    index.value = 0;
    running.value = true;
  };

  const startTutorial = (id: string, step = 0): boolean => {
    if (isMinimalEmbed.value) {
      return false;
    }
    if (isMobile.value && DESKTOP_ONLY_TUTORIAL_IDS.includes(id)) {
      return false;
    }
    if (id === INTRODUCTION_TUTORIAL_ID) {
      start(step);
      return running.value;
    }
    const steps = CONTEXTUAL_TUTORIALS[id];
    if (!steps || steps.length === 0) {
      return false;
    }
    batch(() => {
      mode.value = "contextual";
      activeFeatureId.value = id;
      activeSteps.value = steps;
      index.value = Math.min(Math.max(0, Math.floor(step)), steps.length - 1);
      running.value = true;
    });
    return true;
  };

  const finish = () => {
    running.value = false;
    if (mode.value === "contextual") {
      const featureId = activeFeatureId.value;
      if (featureId) {
        markFeatureSeen(featureId);
      }
      activeFeatureId.value = null;
    } else {
      markOnboardingSeen();
    }
  };

  /**
   * The introduction tour is on screen right now (not a contextual tip).
   * Checked before anything clears `running`, which is what the install
   * prompt is waiting on.
   */
  const introductionTourOpen = () =>
    running.value && mode.value !== "contextual";

  const optOut = () => {
    // Record opt-out first so contextual tours can't restart during teardown.
    // Batched with the "left early" flag so the install prompt sees both at
    // once and doesn't open in the gap.
    batch(() => {
      if (introductionTourOpen()) {
        leftIntroductionEarly.value = true;
      }
      markOptedOut();
      // Also mark the current tour as resolved so it doesn't replay.
      if (mode.value === "contextual") {
        const featureId = activeFeatureId.value;
        if (featureId) {
          markFeatureSeen(featureId);
        }
      } else {
        markOnboardingSeen();
      }
      running.value = false;
      activeFeatureId.value = null;
      skipPromptVisible.value = false;
    });
  };

  const skip = () => {
    if (isMinimalEmbed.value) {
      return;
    }
    batch(() => {
      // Skip on the introduction tour, not a contextual tip, and not Done
      // (that goes through `next` → `finish` and should still chain install).
      if (introductionTourOpen()) {
        leftIntroductionEarly.value = true;
      }
      finish();
      skipPromptVisible.value = true;
    });
  };

  const keepTutorials = () => {
    skipPromptVisible.value = false;
  };

  const acceptPrompt = () => {
    promptVisible.value = false;
    start();
  };

  const dismissPrompt = () => {
    // The flag has to land in the same flush as "seen". The install prompt
    // reacts to that seen flag, and a separate write would let it open on
    // top of "No, thanks".
    batch(() => {
      leftIntroductionEarly.value = true;
      promptVisible.value = false;
      // Treat declining as having resolved the onboarding offer so it isn't
      // re-shown on the next load; the tour stays replayable from Settings.
      markOnboardingSeen();
    });
  };

  const next = () => {
    if (index.value >= activeSteps.value.length - 1) {
      finish();
      return;
    }
    index.value += 1;
  };

  const prev = () => {
    if (index.value > 0) {
      index.value -= 1;
    }
  };

  // Offer the tour once for new users, but only once the reader is open to a
  // chapter and visible — no fullscreen pane (e.g. Today) covering it — and
  // (when logged in) after the profile has loaded, otherwise a returning user
  // could see the offer flash before their recorded completion arrives.
  // Rather than launching the tour unannounced we surface the offer card;
  // accepting it starts the tour. One-shot via `autoStartChecked`.
  //
  // Deliberately NOT armed at construction. `readerVisible` derives from the
  // chapter data, which the client entry (`app/init.tsx`) already awaits before
  // it calls `hydrate()` — so an eager effect flips `promptVisible` true during
  // the client's *first* render while the SSR pass captured it as false
  // (`renderToStringAsync` only re-renders the subtree that threw, and the offer
  // card renders before it). That puts an element in the client tree that the
  // served HTML doesn't have, which is exactly the divergence `hydrate()`
  // reports. `armAutoStart` is called from `AppState.hydrateFromStorage` after
  // the first commit, so the card appears a moment later instead.
  let autoStartArmed = false;
  const armAutoStart = () => {
    if (autoStartArmed) {
      return;
    }
    autoStartArmed = true;
    effect(() => {
      if (autoStartChecked || running.value) {
        return;
      }
      // A `?tutorial=` link asked for a specific tour: launch it (once the
      // reader is ready to be pointed at) in place of the offer card. Checked
      // before the session-link guard because it is an explicit request.
      if (linkedTutorial) {
        if (!readerVisible.value) {
          return;
        }
        autoStartChecked = true;
        startTutorial(linkedTutorial.id, linkedTutorial.step);
        return;
      }
      // Opened via a shared-session invite link: don't auto-launch the onboarding
      // tour over the join. We don't record completion, so the tour still
      // auto-starts on a later visit that isn't a session link.
      if (openedViaContentLink.value) {
        return;
      }
      // A partner-site embed has none of the chrome the tour points at.
      if (isMinimalEmbed.value) {
        return;
      }
      if (!readerVisible.value) {
        return;
      }
      if (login.userId.value && login.profile.value === null) {
        return;
      }
      autoStartChecked = true;
      if (!completed.value && !optedOut.value) {
        promptVisible.value = true;
      }
    });
  };

  return {
    get steps() {
      return activeSteps.value;
    },
    running,
    index,
    activeTutorialId,
    currentStep,
    isLast,
    canGoBack,
    completed,
    optedOut,
    promptVisible,
    skipPromptVisible,
    leftIntroductionEarly,
    featuresSeen,
    start: () => start(),
    startContextual,
    startTutorial,
    next,
    prev,
    finish,
    skip,
    keepTutorials,
    optOut,
    acceptPrompt,
    dismissPrompt,
    hydrateStoredFlags,
    armAutoStart,
  };
}
